import {
  useExistChannel,
  useChannelMembers,
} from '../hooks/store/useChannelHooks';
import { useUnreadCount } from '../hooks/store/useUnreadCount';
import { ChannelCard } from './ChannelCard';
import { CareTeamMember, Channel, Message, Person, User } from '../type';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import {
  displayFullName,
  displayName,
  displayNameInMessage,
  groupName,
  nameInitials,
} from '@xolv-mybrightlink/common';
import { useCurrentChannelGuid } from '../hooks/route/useCurrentChannelGuid';
import { useCareTeamMembers } from '../hooks/store/useCareTeamMembers';
import { IntlShape, useIntl } from 'react-intl';
import { useCurrentUserGuid } from '../../../firebase/useCollection';
import { useUser } from '../hooks/store/useUser';
import { useRemoteI18n } from '../../../remote_i18n/useRemoteI18n';

export const ChannelItem = (props: {
  channelGuid: string;
  onSelect: () => void;
}) => {
  const intl = useIntl();
  const channelGuid = useCurrentChannelGuid();
  const channel = useExistChannel(props.channelGuid);
  const unreadCount = useUnreadCount({
    type: 'channel',
    guid: props.channelGuid,
  });
  const currentUserGuid = useCurrentUserGuid();
  const channelMembers = useChannelMembers(props.channelGuid);
  const careTeamMembers = useCareTeamMembers(props.channelGuid);
  const lastMessageUser = useUser(channel.lastMessage?.authorGuid ?? '');
  const remoteI18n = useRemoteI18n();
  const lastMessage = translateLastMessage(
    channel.lastMessage,
    lastMessageUser,
    intl,
    remoteI18n,
  );
  return (
    <ChannelCard
      avatarName={avatarName(channel, channelMembers, currentUserGuid)}
      avatar={avatar(channel)}
      name={channelName(
        currentUserGuid,
        channel,
        channelMembers,
        careTeamMembers,
        intl,
      )}
      lastMessage={lastMessage}
      lastMessageDate={channel.lastMessage?.createdDate}
      unreadCount={unreadCount}
      selected={channelGuid === props.channelGuid}
      onSelect={props.onSelect}
    />
  );
};

const avatar = (channel: Channel) => {
  const { groupChannel, type } = channel;
  if (groupChannel) {
    return <PeopleIcon sx={{ width: 20, height: 20 }} />;
  } else if (type === 'SCHEDULING_TEAM') {
    return <EventIcon sx={{ width: 20, height: 20 }} />;
  }
  return null;
};

const avatarName = (
  channel: Channel,
  channelMembers: User[],
  currentUserGuid: string,
) => {
  const { groupChannel, type } = channel;
  if (groupChannel || type === 'SCHEDULING_TEAM') {
    return '';
  }
  if (type === 'CARE_TEAM') {
    return nameInitials(findCcmPerson(channel, channelMembers));
  } else if (type === 'CARE_TEAM_ES_NORCAL') {
    const supervisor = channelMembers.sort((m1, m2) =>
      displayName(m1).localeCompare(displayName(m2)),
    )[0];
    return supervisor ? nameInitials(supervisor) : '';
  } else if (type === 'PEER_TO_PEER') {
    const otherMember = channelMembers.find(
      (member) => member.guid !== currentUserGuid,
    );
    return otherMember ? nameInitials(otherMember) : '';
  }
  return '';
};

export const channelName = (
  currentUserGuid: string,
  channel: Channel,
  channelMembers: User[],
  careTeamMembers: CareTeamMember[],
  intl: IntlShape,
) => {
  const schedulingText = intl.formatMessage({
    id: 'care-team.scheduling-team',
  });
  const currentUser = channelMembers.filter(
    (member) => member.guid === currentUserGuid,
  );
  const careGivers = channelMembers.filter((member) => {
    return member.guid !== currentUserGuid && member.role === 'Client';
  });
  const signedUpMembers = channelMembers
    .filter((member) => member.guid !== currentUserGuid)
    .sort((u1, u2) => {
      if (u1.role === 'Client') {
        return -1;
      }
      if (u2.role === 'Client') {
        return 1;
      }
      return 0;
    });

  const { groupChannel, type } = channel;

  if (groupChannel) {
    if (type === 'SCHEDULING_TEAM') {
      return `${schedulingText}, ${groupName(careGivers)}`;
    } else if (type === 'CARE_TEAM') {
      return groupName(signedUpMembers);
    } else {
      return groupName([...careGivers, ...careTeamMembers]);
    }
  } else {
    if (type === 'SCHEDULING_TEAM') {
      return schedulingText;
    } else if (type === 'CARE_TEAM') {
      return displayName(findCcmPerson(channel, channelMembers));
    } else if (type === 'CARE_TEAM_ES_NORCAL') {
      const members = careTeamMembers.length ? careTeamMembers : currentUser;
      return groupName(members);
    } else if (type === 'PEER_TO_PEER') {
      const otherMember = channelMembers.find(
        (member) => member.guid !== currentUserGuid,
      );
      return otherMember ? displayNameInMessage(otherMember) : '';
    }
  }
  return '';
};

const findCcmPerson = (channel: Channel, members: User[]): Person => {
  const ccmGuid = channel.attributes.assignedCcmGuid;
  const ccm = members.find((member) => member.guid === ccmGuid);
  if (ccm) {
    return ccm;
  }
  const { assignedCcmFirstName: firstName, assignedCcmLastName: lastName } =
    channel.attributes;
  return { firstName, lastName };
};

const translateLastMessage = (
  lastMessage: Message | null,
  author: User | null,
  intl: IntlShape,
  remoteI18n: (key: string, args?: string[]) => string,
): string => {
  if (!lastMessage) {
    return '';
  }
  let message: string | null = null;
  if (lastMessage.mediaDeleted) {
    const messageId =
      lastMessage.deletedBy == null
        ? 'messages.deleted.by-policy'
        : 'messages.deleted.by-user';
    message = intl.formatMessage({ id: messageId });
  } else {
    let messageId: string | null = null;
    switch (lastMessage.messageType) {
      case 'IMAGE':
        messageId = 'message.share-image';
        break;
      case 'VIDEO':
        messageId = 'message.share-video';
        break;
      case 'PDF':
        messageId = 'message.share-pdf';
        break;
      case 'WORD':
        messageId = 'message.share-word';
        break;
      default:
        break;
    }

    if (messageId && author) {
      const fullName = displayFullName(author);
      message = intl.formatMessage({ id: messageId }, { name: fullName });
    }
  }

  if (!message && lastMessage.contentKey) {
    return remoteI18n(lastMessage.contentKey, lastMessage.contentParams ?? []);
  }

  return message ?? lastMessage.content;
};
